import type { ReactNode } from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.js';
import { cn } from '@/lib/utils.js';

import { HintActionFooter } from './HintActionFooter.js';
import { HINTS } from './hint-config.js';
import { HintContent } from './HintContent.js';
import { skipAllHints } from './hint-store.js';

export interface HintDialogProps {
  id: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Modal help for an interactive hint beacon. Footer offers "Got it" (this tip only) and
 * "Skip all tips" (marks every configured node seen).
 */
export function HintDialog({ id, open, onClose }: HintDialogProps): ReactNode {
  const hint = HINTS[id];
  if (!hint) return null;

  /** Ends the whole tour and closes. */
  function handleSkipTour(): void {
    skipAllHints(Object.keys(HINTS));
    onClose();
  }

  const wideVideo = hint.videoAspect === 'wide';

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent
        aria-describedby={undefined}
        className={cn(wideVideo ? 'max-w-3xl' : 'max-w-xl')}
      >
        <DialogHeader>
          <DialogTitle>{hint.title}</DialogTitle>
        </DialogHeader>
        <HintContent hint={hint} />
        <HintActionFooter onGotIt={onClose} onSkipTour={handleSkipTour} />
      </DialogContent>
    </Dialog>
  );
}
